import { useEffect, useState } from 'react';
import Countdown from './shared/Countdown';
import {
  PampasGrass,
  SunMoon,
  DriedBranch,
  BohoDivider,
  BohoSun,
  Laurel,
  WeddingRings,
  ArrowDown,
} from './shared/Decorations';
import {
  IconShirt,
  IconGift,
  IconCamera,
  IconChurch,
  IconMapPin,
  IconBook,
} from '../components/ui/Icons';
import Gallery from './shared/Gallery';
import AudioPlayer from '../components/AudioPlayer';

// Paleta terracota
const TERRA = '#b56a44';
const TERRA_SOFT = '#d4a084';
const SAND = '#f5ede3';
const SAND_DARK = '#ede0d0';
const OLIVE = '#7d8a6a';
const DARK = '#4a3828';
const MUTED = '#8a7566';
const CREAM = '#fbf6ef';
const CREAM_RGB = '251,246,239';
const GOLD = '#e9c99b';

const serif = "'Italiana', serif";
const script = "'Cormorant Garamond', serif";
const body = "'Karla', sans-serif";

const IconCityHall = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 21h16M4 21V10l8-4 8 4v11M9 21v-6h6v6" />
    <path d="M12 4V2M9 6.5h6" />
    <circle cx="12" cy="13" r="0.8" fill="currentColor" stroke="none" />
  </svg>
);

/* ==============================================================
   ESTILOS Y ANIMACIONES (todo en un solo lugar)
   ============================================================== */
const BohoStyles = () => (
  <style>{`
    /* --- Pétalos: 3 capas de movimiento (caída, vaivén, giro 3D) --- */
    @keyframes bb-fall {
      0%   { transform: translate3d(0, -14vh, 0); opacity: 0; }
      8%   { opacity: var(--o); }
      92%  { opacity: var(--o); }
      100% { transform: translate3d(0, 114vh, 0); opacity: 0; }
    }
    @keyframes bb-sway {
      0%   { transform: translateX(calc(var(--drift) * -1)); }
      100% { transform: translateX(var(--drift)); }
    }
    @keyframes bb-spin {
      0%   { transform: rotateZ(var(--r0)) rotateX(0deg) rotateY(0deg); }
      100% { transform: rotateZ(calc(var(--r0) + 160deg)) rotateX(360deg) rotateY(200deg); }
    }
    .bb-petal {
      position: absolute;
      top: 0;
      animation: bb-fall var(--dur) linear infinite;
      animation-delay: var(--delay);
      will-change: transform, opacity;
    }
    .bb-petal-sway {
      perspective: 400px;
      animation: bb-sway var(--sway) ease-in-out infinite alternate;
      animation-delay: var(--delay);
    }
    .bb-petal-spin {
      display: block;
      transform-style: preserve-3d;
      animation: bb-spin var(--spin) linear infinite;
      animation-delay: var(--delay);
      filter: drop-shadow(0 2px 3px rgba(181,106,68,0.18));
    }

    /* --- Polvo dorado en el hero --- */
    @keyframes bb-twinkle {
      0%, 100% { opacity: 0;   transform: translateY(0) scale(0.4); }
      50%      { opacity: 0.95; transform: translateY(-14px) scale(1); }
    }
    .bb-spark {
      position: absolute;
      border-radius: 9999px;
      background: radial-gradient(circle, #fff 0%, ${GOLD} 45%, transparent 72%);
      animation: bb-twinkle var(--t) ease-in-out infinite;
      animation-delay: var(--d);
    }

    /* --- Entrada del hero (un solo momento orquestado) --- */
    @keyframes bb-rise {
      from { opacity: 0; transform: translateY(24px); filter: blur(6px); }
      to   { opacity: 1; transform: none;             filter: blur(0); }
    }
    .bb-rise {
      opacity: 0;
      animation: bb-rise 1.4s cubic-bezier(0.22, 1, 0.36, 1) forwards;
      animation-delay: var(--rd, 0s);
    }
    @keyframes bb-kenburns {
      from { transform: scale(1.12); }
      to   { transform: scale(1); }
    }
    .bb-kenburns { animation: bb-kenburns 22s ease-out forwards; }

    /* --- Ramas laterales --- */
    @keyframes bb-branch {
      0%, 100% { transform: rotate(-2.5deg); }
      50%      { transform: rotate(2.5deg); }
    }
    .bb-branch {
      transform-origin: 50% 100%;
      animation: bb-branch var(--b, 9s) ease-in-out infinite;
    }

    /* --- Brillo suave en el ícono de sol de la frase --- */
    @keyframes bb-glow {
      0%, 100% { filter: drop-shadow(0 0 0 rgba(181,106,68,0)); }
      50%      { filter: drop-shadow(0 0 8px rgba(233,201,155,0.9)); }
    }
    .bb-glow { animation: bb-glow 4s ease-in-out infinite; }

    /* Accesibilidad: sin movimiento si el usuario lo prefiere */
    @media (prefers-reduced-motion: reduce) {
      .bb-petal, .bb-petal-sway, .bb-petal-spin, .bb-spark,
      .bb-kenburns, .bb-branch, .bb-glow { animation: none !important; }
      .bb-petal, .bb-spark { display: none; }
      .bb-rise { animation: none !important; opacity: 1; filter: none; transform: none; }
    }
  `}</style>
);

/* ==============================================================
   PÉTALOS CAYENDO
   - 3 capas de profundidad (lejos / medio / cerca)
   - Paleta terracota, blush y crema
   - Vaivén lateral + giro 3D, empiezan ya distribuidos en pantalla
   ============================================================== */
const PETAL_COLORS = ['#e8b4a0', '#d4a084', '#f0c9b8', '#c98466', '#f6d9cc', '#e2a58c', '#ecbfae'];

const PetalShape = ({ color }) => (
  <svg viewBox="0 0 24 28" width="100%" height="100%" aria-hidden="true">
    <path
      d="M12 1C18 5 21.5 12 17 19.5C15.4 22.4 13.6 24.8 12 27C10.4 24.8 8.6 22.4 7 19.5C2.5 12 6 5 12 1Z"
      fill={color}
    />
    {/* vena central y brillo */}
    <path d="M12 5C12.4 12 12.2 19 12 25" stroke="#fff" strokeOpacity="0.35" strokeWidth="0.6" fill="none" />
    <ellipse cx="9.6" cy="10" rx="2" ry="5" fill="#fff" fillOpacity="0.22" transform="rotate(-12 9.6 10)" />
  </svg>
);

const FallingPetals = ({ count = 26 }) => {
  const [petals, setPetals] = useState([]);

  useEffect(() => {
    const layers = [
      // lejos: pequeños, lentos, tenues
      { size: [8, 12],  dur: [22, 30], o: 0.28, blur: 1.2, share: 0.4 },
      // medio
      { size: [12, 18], dur: [15, 22], o: 0.45, blur: 0,   share: 0.4 },
      // cerca: grandes, rápidos, algo desenfocados
      { size: [20, 28], dur: [11, 15], o: 0.55, blur: 1.6, share: 0.2 },
    ];
    const rnd = (a, b) => a + Math.random() * (b - a);

    const list = [];
    let id = 0;
    layers.forEach((l) => {
      const n = Math.round(count * l.share);
      for (let i = 0; i < n; i++) {
        const dur = rnd(l.dur[0], l.dur[1]);
        list.push({
          id: id++,
          left: rnd(-2, 100),
          size: rnd(l.size[0], l.size[1]),
          dur,
          delay: -rnd(0, dur), // negativo: ya están cayendo al cargar
          sway: rnd(3.5, 7),
          drift: rnd(18, 55),
          spin: rnd(5, 11),
          r0: rnd(0, 360),
          o: l.o,
          blur: l.blur,
          color: PETAL_COLORS[Math.floor(Math.random() * PETAL_COLORS.length)],
        });
      }
    });
    setPetals(list);
  }, [count]);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 30 }} aria-hidden="true">
      {petals.map((p) => (
        <div
          key={p.id}
          className="bb-petal"
          style={{
            left: `${p.left}%`,
            '--dur': `${p.dur}s`,
            '--delay': `${p.delay}s`,
            '--o': p.o,
          }}
        >
          <div
            className="bb-petal-sway"
            style={{ '--sway': `${p.sway}s`, '--drift': `${p.drift}px`, '--delay': `${p.delay}s` }}
          >
            <span
              className="bb-petal-spin"
              style={{
                width: p.size,
                height: p.size * 1.15,
                '--spin': `${p.spin}s`,
                '--r0': `${p.r0}deg`,
                '--delay': `${p.delay}s`,
                filter: p.blur ? `blur(${p.blur}px)` : undefined,
              }}
            >
              <PetalShape color={p.color} />
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

/* ==============================================================
   POLVO DORADO (hero)
   ============================================================== */
const GoldDust = ({ count = 16 }) => {
  const [dots, setDots] = useState([]);
  useEffect(() => {
    setDots(
      Array.from({ length: count }, (_, i) => ({
        id: i,
        left: 4 + Math.random() * 92,
        top: 6 + Math.random() * 78,
        size: 3 + Math.random() * 5,
        t: 3.5 + Math.random() * 4,
        d: Math.random() * 6,
      }))
    );
  }, [count]);

  return (
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 10 }} aria-hidden="true">
      {dots.map((s) => (
        <span
          key={s.id}
          className="bb-spark"
          style={{
            left: `${s.left}%`,
            top: `${s.top}%`,
            width: s.size,
            height: s.size,
            '--t': `${s.t}s`,
            '--d': `${s.d}s`,
          }}
        />
      ))}
    </div>
  );
};

/* ==============================================================
   RAMAS LATERALES (solo escritorio)
   ============================================================== */
const LEAVES = Array.from({ length: 9 }, (_, i) => ({
  y: 395 - i * 42,
  side: i % 2 === 0 ? -1 : 1,
  rx: 16 - i * 0.9,
  color: i % 3 === 0 ? TERRA_SOFT : OLIVE,
}));

const Branch = ({ mirror = false, className = '', style = {}, sway = '9s' }) => (
  <svg
    viewBox="0 0 120 420"
    className={`bb-branch ${className}`}
    style={{ ...style, '--b': sway, transform: mirror ? 'scaleX(-1)' : undefined }}
    aria-hidden="true"
  >
    <path d="M60 420C50 320 70 220 55 120C50 70 58 30 62 0" stroke={OLIVE} strokeWidth="1.6" fill="none" strokeLinecap="round" />
    {LEAVES.map((l, i) => {
      const x = 58 + (i % 2 ? 2 : -2);
      return (
        <ellipse
          key={i}
          cx={x + l.side * 15}
          cy={l.y}
          rx={l.rx}
          ry={5}
          fill={l.color}
          opacity={0.85}
          transform={`rotate(${l.side * -32} ${x} ${l.y})`}
        />
      );
    })}
    <circle cx="62" cy="6" r="5" fill={TERRA_SOFT} />
    <circle cx="62" cy="6" r="2" fill={TERRA} opacity="0.8" />
  </svg>
);

const FloralSideFrame = ({ position = 'left' }) => {
  const isLeft = position === 'left';
  return (
    <div
      className={`fixed top-0 h-full w-40 pointer-events-none hidden lg:block ${isLeft ? 'left-0' : 'right-0'}`}
      style={{ zIndex: 0 }}
      aria-hidden="true"
    >
      <Branch
        mirror={!isLeft}
        sway={isLeft ? '9s' : '11s'}
        className="absolute bottom-0 w-28 h-[420px] opacity-25"
        style={{ [position]: '-10px' }}
      />
      <Branch
        mirror={isLeft}
        sway={isLeft ? '12s' : '8s'}
        className="absolute top-24 w-20 h-[300px] opacity-[0.18]"
        style={{ [position]: '30px' }}
      />
    </div>
  );
};

/* ==============================================================
   HELPERS
   ============================================================== */
const Eyebrow = ({ children, tracking = '0.6em', className = 'mb-4', color = TERRA, style = {} }) => (
  <p
    className={`text-[10px] uppercase ${className}`}
    style={{ color, fontFamily: body, fontWeight: 600, letterSpacing: tracking, ...style }}
  >
    {children}
  </p>
);

const cardStyle = (shadow = '0 16px 40px -20px') => ({
  background: CREAM,
  border: `1px solid ${TERRA}30`,
  boxShadow: `${shadow} ${TERRA}40`,
});

const IconBadge = ({ children, size = 'w-14 h-14', inner = 'w-6 h-6', border = false, className = '' }) => (
  <div
    className={`${size} rounded-full grid place-items-center ${className}`}
    style={{ background: SAND_DARK, color: TERRA, border: border ? `1px solid ${TERRA}50` : undefined }}
  >
    <div className={inner}>{children}</div>
  </div>
);

const CeremonyCard = ({ icon, label, name, address, time }) => (
  <div className="relative rounded-[80px_80px_20px_20px] text-center px-8 pt-14 pb-12" style={cardStyle()}>
    <div className="absolute top-6 left-1/2 -translate-x-1/2">
      <IconBadge border>{icon}</IconBadge>
    </div>
    <Eyebrow tracking="0.5em" className="mt-6 mb-5">{label}</Eyebrow>
    {name && (
      <p className="text-2xl mb-2 leading-snug" style={{ fontFamily: serif, color: DARK, fontWeight: 400 }}>
        {name}
      </p>
    )}
    {address && (
      <p className="text-base mb-3 leading-relaxed" style={{ fontFamily: script, color: MUTED, fontWeight: 500 }}>
        {address}
      </p>
    )}
    {time && (
      <p className="text-lg italic mt-4" style={{ fontFamily: script, color: TERRA, fontWeight: 500 }}>
        {time}
      </p>
    )}
  </div>
);

const DetailCard = ({ icon, label, children }) => (
  <div
    className="text-center px-6 py-10 rounded-[60px_60px_20px_20px] flex flex-col items-center"
    style={cardStyle('0 12px 30px -20px')}
  >
    <IconBadge className="mb-5">{icon}</IconBadge>
    <Eyebrow tracking="0.5em">{label}</Eyebrow>
    {children}
  </div>
);

function useGoogleFonts() {
  useEffect(() => {
    const id = 'gf-font-boho';
    if (document.getElementById(id)) return;
    const el = document.createElement('link');
    el.id = id;
    el.rel = 'stylesheet';
    el.href =
      'https://fonts.googleapis.com/css2?family=Italiana&family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Karla:wght@300;400;500;600&display=swap';
    document.head.appendChild(el);
  }, []);
}

/* ==============================================================
   COMPONENTE PRINCIPAL
   ============================================================== */
export default function BodaBoho({ data, title, media = [], cover = null, audio = null }) {
  useGoogleFonts();

  // Detecta si la portada es vertical u horizontal
  const [portrait, setPortrait] = useState(null);
  useEffect(() => {
    if (!cover?.url) return;
    const im = new Image();
    im.onload = () => setPortrait(im.naturalHeight >= im.naturalWidth);
    im.src = cover.url;
  }, [cover?.url]);
  const isLandscape = portrait === false;

  const d = data || {};
  const bride = d.bride || 'Lucía';
  const groom = d.groom || 'Mateo';
  const date = d.date ? new Date(d.date) : null;

  const dateShort = date
    ? date.toLocaleDateString('es-PE', { day: '2-digit', month: 'long', year: 'numeric' })
    : null;
  const dateTiny = date
    ? date.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : null;

  const hasCover = !!cover?.url;
  const hasCivil = !!(d.civilPlace || d.civilAddress);
  const hasChurch = !!(d.church || d.churchAddress);
  const hasReception = !!(d.place || d.address);
  const hasDetails = d.dressCode || d.giftRegistry || d.hashtag;

  const verse = d.verse || 'El amor no consiste en mirarse el uno al otro, sino en mirar juntos en la misma dirección.';
  const verseRef = d.verseRef || 'Antoine de Saint-Exupéry';

  // Colores del hero según haya foto o no
  const heroText = hasCover ? '#ffffff' : DARK;
  const heroAccent = hasCover ? 'rgba(255,255,255,0.95)' : TERRA;
  const heroLine = hasCover ? 'rgba(255,255,255,0.6)' : TERRA;
  const shadow = (blur, a = 0.5) => (hasCover ? `0 4px ${blur}px rgba(0,0,0,${a})` : 'none');
  const rise = (s) => ({ '--rd': `${s}s` });

  return (
    <div
      className="min-h-screen w-full relative overflow-hidden"
      style={{ background: `linear-gradient(180deg, ${CREAM} 0%, ${SAND} 50%, ${CREAM} 100%)`, color: DARK }}
    >
      <BohoStyles />

      {/* Efectos de fondo */}
      <FloralSideFrame position="left" />
      <FloralSideFrame position="right" />
      <FallingPetals />

      {/* ============================================================
          HERO
          ============================================================ */}
      <section className="relative overflow-hidden min-h-[100svh] flex items-center">
        {/* Viñeta */}
        <div
          className="absolute inset-0 pointer-events-none z-10"
          style={{
            background:
              'radial-gradient(ellipse at center 40%, transparent 0%, transparent 70%, rgba(245,237,227,0.25) 100%)',
          }}
        />

        {/* ---------- Fondo ---------- */}
        {hasCover ? (
          <>
            {/* Móvil + foto vertical: pantalla completa */}
            <img
              src={cover.url}
              alt=""
              className={`bb-kenburns absolute inset-0 w-full h-full object-cover ${
                isLandscape ? 'hidden' : 'lg:hidden'
              }`}
              style={{ objectPosition: 'center 25%' }}
            />
            {/* Fondo desenfocado con la misma foto */}
            <img
              src={cover.url}
              alt=""
              aria-hidden="true"
              className={`absolute inset-0 w-full h-full object-cover scale-110 blur-2xl ${
                isLandscape ? 'block' : 'hidden lg:block'
              }`}
            />
            {/* Overlay más ligero (fondo menos oscuro) */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(180deg, rgba(20,15,5,0.15) 0%, rgba(20,15,5,0.18) 30%, rgba(20,15,5,0.25) 60%, rgba(20,15,5,0.25) 88%, rgba(20,15,5,0.15) 97%, rgba(20,15,5,0) 100%)',
              }}
            />
          </>
        ) : (
          <>
            <PampasGrass color={TERRA} className="absolute top-8 -left-4 w-20 h-52 sm:w-28 sm:h-72 opacity-60" />
            <PampasGrass
              color={OLIVE}
              className="absolute top-16 -right-2 w-16 h-44 sm:w-24 sm:h-64 opacity-40 -scale-x-100"
            />
          </>
        )}

        <GoldDust />

        {/* ---------- Contenido ---------- */}
        <div
          className={`relative z-20 w-full max-w-[90rem] mx-auto px-6 pt-16 pb-28 sm:pb-32 lg:py-16 grid items-center gap-10 lg:gap-14 ${
            hasCover ? (isLandscape ? 'lg:grid-cols-[1.8fr_1fr]' : 'lg:grid-cols-2') : ''
          }`}
        >
          {/* Foto completa, sin recortar */}
          {hasCover && (
            <div
              className={`bb-rise justify-center px-2 ${isLandscape ? 'flex' : 'hidden lg:flex'}`}
              style={rise(0.1)}
            >
              <div className="relative w-full" style={isLandscape ? { maxWidth: 860 } : undefined}>
                {/* Marco decorativo desfasado */}
                <div
                  className="absolute inset-0 rounded-[28px] translate-x-3 translate-y-3 sm:translate-x-4 sm:translate-y-4"
                  style={{ border: `1.5px solid ${GOLD}`, opacity: 0.85 }}
                  aria-hidden="true"
                />
                {isLandscape ? (
                  /* Visor 4:3 (más alto): la foto se ve completa dentro, sin recortar */
                  <div
                    className="relative w-full aspect-[4/3] max-h-[80vh] overflow-hidden rounded-[28px]"
                    style={{
                      boxShadow: '0 30px 70px -20px rgba(0,0,0,0.55)',
                      border: '6px solid rgba(255,255,255,0.92)',
                    }}
                  >
                    <img
                      src={cover.url}
                      alt=""
                      aria-hidden="true"
                      className="absolute inset-0 w-full h-full object-cover scale-110 blur-xl"
                    />
                    <img
                      src={cover.url}
                      alt={`${bride} y ${groom}`}
                      className="absolute inset-0 w-full h-full object-contain"
                    />
                  </div>
                ) : (
                  <img
                    src={cover.url}
                    alt={`${bride} y ${groom}`}
                    className="relative block w-auto h-auto max-w-full max-h-[82vh] object-contain rounded-[28px]"
                    style={{
                      boxShadow: '0 30px 70px -20px rgba(0,0,0,0.55)',
                      border: '6px solid rgba(255,255,255,0.92)',
                    }}
                  />
                )}
              </div>
            </div>
          )}

          {/* Texto */}
          <div className="text-center max-w-3xl mx-auto w-full">
            {!hasCover && (
              <div className="bb-rise" style={rise(0.1)}>
                <SunMoon color={TERRA} className="w-32 h-24 mx-auto mb-8 opacity-80" />
              </div>
            )}

            <div className="bb-rise" style={rise(0.2)}>
              <Eyebrow tracking="0.7em" className="text-[10px] sm:text-xs mb-6" color={heroAccent}>
                Nuestra Boda
              </Eyebrow>
              <WeddingRings color={heroAccent} className="w-20 h-14 mx-auto mb-8 opacity-90" />
            </div>

            <h1
              className="bb-rise text-6xl sm:text-7xl lg:text-7xl xl:text-8xl leading-[1.05] mb-3"
              style={{
                ...rise(0.55),
                fontFamily: serif,
                color: heroText,
                fontWeight: 400,
                letterSpacing: '0.02em',
                textShadow: shadow(40),
              }}
            >
              {bride}
            </h1>

            <div className="bb-rise my-5 flex items-center justify-center gap-5" style={rise(0.85)}>
              <div className="h-px w-16" style={{ background: heroLine, opacity: hasCover ? 0.7 : 0.4 }} />
              <span className="text-4xl italic" style={{ fontFamily: script, color: heroAccent, fontWeight: 500 }}>
                &amp;
              </span>
              <div className="h-px w-16" style={{ background: heroLine, opacity: hasCover ? 0.7 : 0.4 }} />
            </div>

            <h1
              className="bb-rise text-6xl sm:text-7xl lg:text-7xl xl:text-8xl leading-[1.05] mb-8"
              style={{
                ...rise(1.1),
                fontFamily: serif,
                color: heroText,
                fontWeight: 400,
                letterSpacing: '0.02em',
                textShadow: shadow(40),
              }}
            >
              {groom}
            </h1>

            <div className="bb-rise" style={rise(1.5)}>
              <div className="flex justify-center mb-8">
                <BohoDivider color={hasCover ? 'rgba(255,255,255,0.9)' : TERRA} className="w-56 h-6" />
              </div>

              {dateShort && (
                <div className="inline-block">
                  <Eyebrow
                    tracking="0.5em"
                    className="text-[10px] sm:text-xs mb-4"
                    color={hasCover ? 'rgba(255,255,255,0.9)' : TERRA}
                  >
                    Nos casamos el
                  </Eyebrow>
                  <p
                    className="text-2xl sm:text-3xl tracking-[0.15em] uppercase"
                    style={{
                      fontFamily: body,
                      color: heroText,
                      fontWeight: 500,
                      textShadow: shadow(20),
                    }}
                  >
                    {dateShort}
                  </p>
                </div>
              )}

              {title && (
                <p
                  className="mt-8 text-xl sm:text-2xl italic max-w-2xl mx-auto leading-relaxed"
                  style={{
                    fontFamily: script,
                    color: hasCover ? 'rgba(255,255,255,0.9)' : MUTED,
                    fontWeight: 500,
                  }}
                >
                  "{title}"
                </p>
              )}

              <div className="flex justify-center mt-10">
                <ArrowDown
                  color={hasCover ? 'rgba(255,255,255,0.85)' : TERRA}
                  className="w-6 h-12 opacity-60 animate-bounce"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Fade a crema pegado al borde inferior */}
        {hasCover && (
          <div
            className="absolute bottom-0 left-0 right-0 h-10 sm:h-12 lg:h-14 pointer-events-none z-10"
            style={{
              background: `linear-gradient(180deg,
                rgba(${CREAM_RGB},0) 0%,
                rgba(${CREAM_RGB},0.12) 25%,
                rgba(${CREAM_RGB},0.4) 55%,
                rgba(${CREAM_RGB},0.8) 82%,
                ${CREAM} 100%)`,
            }}
          />
        )}
      </section>

      {/* ============================================================
          FRASE
          ============================================================ */}
      <section className="px-6 py-16 relative">
        <div className="max-w-3xl mx-auto">
          <div
            className="relative px-8 sm:px-16 py-14 text-center rounded-3xl"
            style={cardStyle('0 24px 60px -30px')}
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bb-glow">
              <BohoSun color={TERRA} className="w-8 h-8" />
            </div>

            <p
              className="text-7xl leading-none mb-3 select-none"
              style={{ color: TERRA, fontFamily: serif, opacity: 0.25 }}
            >
              "
            </p>

            <p className="text-xl sm:text-2xl leading-[1.7] italic" style={{ fontFamily: script, color: DARK, fontWeight: 500 }}>
              {verse}
            </p>

            <div className="my-8 flex justify-center">
              <DriedBranch color={TERRA} className="w-40 h-8 opacity-70" />
            </div>

            <Eyebrow tracking="0.4em" className="text-xs sm:text-sm">{verseRef}</Eyebrow>
          </div>
        </div>
      </section>

      {/* ============================================================
          CUENTA REGRESIVA
          ============================================================ */}
      {date && (
        <section className="px-6 py-16">
          <div className="max-w-3xl mx-auto text-center">
            <Eyebrow className="text-xs">Faltan</Eyebrow>
            <p className="text-3xl sm:text-4xl mb-12 italic" style={{ fontFamily: script, color: DARK, fontWeight: 500 }}>
              para nuestro gran día
            </p>

            <Countdown target={date} accent={TERRA} labelColor={MUTED} />

            <div className="flex justify-center mt-12">
              <Laurel color={TERRA} className="w-40 h-16 opacity-60" />
            </div>
          </div>
        </section>
      )}

      {/* ============================================================
          NUESTRA HISTORIA
          ============================================================ */}
      {d.story && (
        <section className="px-6 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <div className="flex justify-center mb-5">
              <div className="w-7 h-7" style={{ color: TERRA }}>
                <IconBook className="w-full h-full" />
              </div>
            </div>
            <Eyebrow className="mb-5">Nuestra Historia</Eyebrow>
            <div className="flex justify-center mb-8">
              <BohoDivider color={TERRA} className="w-40 h-5" />
            </div>
            <p
              className="text-lg sm:text-xl leading-[1.9] italic whitespace-pre-line"
              style={{ fontFamily: script, color: DARK, fontWeight: 500, opacity: 0.9 }}
            >
              {d.story}
            </p>
          </div>
        </section>
      )}

      {/* ============================================================
          GALERÍA
          ============================================================ */}
      <Gallery
        media={media}
        category="Matrimonios"
        accent={TERRA}
        text={DARK}
        muted={MUTED}
        title="Nuestros Momentos"
        subtitle="Aventuras Juntos"
        headingFont={serif}
      />

      {/* ============================================================
          CEREMONIAS
          ============================================================ */}
      {(hasCivil || hasChurch) && (
        <section className="relative px-6 py-16">
          <div className="max-w-5xl mx-auto">
            <PampasGrass
              color={TERRA}
              className="absolute -left-6 top-1/3 w-16 h-40 opacity-30 -scale-x-100 hidden sm:block"
            />

            <div className="text-center mb-14">
              <Eyebrow>Itinerario</Eyebrow>
              <p
                className="text-3xl sm:text-4xl mb-5"
                style={{ fontFamily: serif, color: DARK, fontWeight: 400, letterSpacing: '0.03em' }}
              >
                Ceremonias
              </p>
              <div className="flex justify-center">
                <BohoDivider color={TERRA} className="w-52 h-6" />
              </div>
            </div>

            <div className={`grid gap-6 ${hasCivil && hasChurch ? 'md:grid-cols-2' : ''} max-w-3xl mx-auto`}>
              {hasCivil && (
                <CeremonyCard
                  icon={<IconCityHall className="w-full h-full" />}
                  label="Civil"
                  name={d.civilPlace}
                  address={d.civilAddress}
                  time={d.civilTime}
                />
              )}
              {hasChurch && (
                <CeremonyCard
                  icon={<IconChurch className="w-full h-full" />}
                  label="Religiosa"
                  name={d.church}
                  address={d.churchAddress}
                  time={d.churchTime || d.time}
                />
              )}
            </div>
          </div>
        </section>
      )}

      {/* ============================================================
          RECEPCIÓN
          ============================================================ */}
      {hasReception && (
        <section className="px-6 py-16">
          <div className="max-w-3xl mx-auto">
            <div
              className="relative rounded-[100px_100px_30px_30px] text-center px-8 sm:px-16 pt-20 pb-16"
              style={cardStyle('0 24px 60px -30px')}
            >
              <div className="absolute top-8 left-1/2 -translate-x-1/2">
                <IconBadge size="w-16 h-16" inner="w-7 h-7" border>
                  <IconMapPin className="w-full h-full" />
                </IconBadge>
              </div>

              <Eyebrow className="mb-6">Recepción</Eyebrow>

              {d.place && (
                <p className="text-3xl sm:text-4xl mb-4 leading-tight" style={{ fontFamily: serif, color: DARK, fontWeight: 400 }}>
                  {d.place}
                </p>
              )}
              {d.address && (
                <p className="text-lg mb-5 leading-relaxed max-w-xl mx-auto" style={{ fontFamily: script, color: MUTED, fontWeight: 500 }}>
                  {d.address}
                </p>
              )}
              {d.receptionTime && (
                <p className="text-xl italic" style={{ fontFamily: script, color: TERRA, fontWeight: 500 }}>
                  {d.receptionTime}
                </p>
              )}

              <div className="mt-10 flex justify-center">
                <SunMoon color={TERRA} className="w-32 h-20 opacity-60" />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============================================================
          DETALLES
          ============================================================ */}
      {hasDetails && (
        <section className="px-6 py-16">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Eyebrow>Detalles</Eyebrow>
              <div className="flex justify-center">
                <BohoDivider color={TERRA} className="w-40 h-5" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {d.dressCode && (
                <DetailCard icon={<IconShirt className="w-full h-full" />} label="Dress Code">
                  <p className="text-base leading-snug" style={{ fontFamily: script, color: DARK, fontWeight: 500 }}>
                    {d.dressCode}
                  </p>
                </DetailCard>
              )}

              {d.giftRegistry && (
                <DetailCard icon={<IconGift className="w-full h-full" />} label="Mesa de Regalos">
                  <a
                    href={d.giftRegistry}
                    target="_blank"
                    rel="noreferrer"
                    className="text-base underline decoration-dotted hover:opacity-70 transition"
                    style={{ fontFamily: script, color: DARK, fontWeight: 500 }}
                  >
                    Ver lista
                  </a>
                </DetailCard>
              )}

              {d.hashtag && (
                <DetailCard icon={<IconCamera className="w-full h-full" />} label="Comparte tus fotos">
                  <p className="text-base leading-snug" style={{ fontFamily: body, color: DARK, fontWeight: 500 }}>
                    {d.hashtag}
                  </p>
                </DetailCard>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ============================================================
          MENSAJE
          ============================================================ */}
      {d.description && (
        <section className="px-6 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <DriedBranch color={TERRA} className="w-32 h-6 opacity-70" />
            </div>

            <Eyebrow className="mb-5">Un mensaje para ti</Eyebrow>

            <p className="text-xl sm:text-2xl leading-[1.8] italic" style={{ fontFamily: script, color: DARK, fontWeight: 500 }}>
              "{d.description}"
            </p>

            <div className="flex justify-center mt-8">
              <DriedBranch color={TERRA} className="w-32 h-6 opacity-70 -scale-x-100" />
            </div>
          </div>
        </section>
      )}

      {/* ============================================================
          NOTAS
          ============================================================ */}
      {d.notes && (
        <section className="px-6 py-10">
          <div className="max-w-xl mx-auto text-center">
            <p className="text-sm leading-relaxed" style={{ fontFamily: script, color: MUTED, fontWeight: 500 }}>
              {d.notes}
            </p>
          </div>
        </section>
      )}

      {/* ============================================================
          CIERRE
          ============================================================ */}
      <section className="relative px-6 py-20 text-center">
        <PampasGrass color={TERRA} className="absolute bottom-0 left-4 w-16 h-40 opacity-40 sm:w-24 sm:h-56" />
        <PampasGrass color={OLIVE} className="absolute bottom-0 right-4 w-16 h-40 opacity-40 sm:w-24 sm:h-56 -scale-x-100" />

        <div className="max-w-2xl mx-auto relative">
          <SunMoon color={TERRA} className="w-28 h-20 mx-auto mb-8 opacity-70" />

          <p className="text-2xl sm:text-3xl italic leading-[1.7]" style={{ fontFamily: script, color: DARK, fontWeight: 500 }}>
            Tu presencia es el regalo más valioso.
            <br />
            ¡Te esperamos para celebrar juntos!
          </p>

          {dateTiny && (
            <Eyebrow tracking="0.5em" className="mt-12 text-xs">
              {bride} &amp; {groom} · {dateTiny}
            </Eyebrow>
          )}
        </div>
      </section>

      {/* ============================================================
          FOOTER
          ============================================================ */}
      <footer className="pb-12 text-center">
        <div className="flex justify-center mb-4">
          <BohoDivider color={TERRA} className="w-32 h-5 opacity-60" />
        </div>
        <p className="text-xs" style={{ fontFamily: body, color: MUTED, letterSpacing: '0.15em', fontWeight: 500 }}>
          Invitación creada con{' '}
          <b style={{ color: TERRA, fontFamily: serif, fontWeight: 600 }}>Go Fiesta</b>
        </p>
      </footer>

      {/* ============================================================
          REPRODUCTOR DE MÚSICA
          ============================================================ */}
      {audio && <AudioPlayer audio={audio} accent={TERRA} />}
    </div>
  );
}